import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }
    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const resp = await axios.get(`${API}/auth/verify-email?token=${token}`);
      setStatus('success');
      setMessage(resp.data.message || 'Email verified successfully!');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. The link may be expired or invalid.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1920&q=80"
          alt="Books background"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/90 to-red-900/30" />
      </div>

      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-black/80 backdrop-blur-lg p-8 rounded-lg shadow-2xl border border-white/10 text-center">
          <div className="flex items-center justify-center mb-6">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/static/scb-logo.png`}
              alt="Seven Chapter Books"
              className="h-28 sm:h-32 w-auto cursor-pointer"
              style={{ filter: 'drop-shadow(0 4px 24px rgba(0, 0, 0, 0.85))', imageRendering: 'auto' }}
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              onClick={() => navigate('/')}
            />
          </div>

          {status === 'loading' && (
            <div data-testid="verify-loading">
              <Loader2 className="h-12 w-12 text-red-500 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Verifying Your Email...</h2>
              <p className="text-gray-400">Please wait a moment.</p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="verify-success">
              <div className="bg-green-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Email Verified!</h2>
              <p className="text-gray-400 mb-6">{message}</p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6"
                data-testid="verify-go-to-login"
              >
                Go to Login
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="verify-error">
              <div className="bg-red-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Verification Failed</h2>
              <p className="text-gray-400 mb-6">{message}</p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6"
                data-testid="verify-go-to-login"
              >
                Go to Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
